import { GraphQLRequestClient } from "@sitecore-jss/sitecore-jss-nextjs/graphql";

/**
 * Represents a request to query an item by its ID and language.
 */
export interface ItemQueryRequest {
  id: string; // The ID of the item to query.
  language: string; // The language of the item to query.
}

/**
 * Represents the response for an item URL query.
 */
export interface ItemUrlResponse {
  item?: {
    url: {
      hostName?: string; // The host name of the item's URL (optional).
      path: string; // The path of the item's URL.
      scheme?: string; // The scheme of the item's URL (optional).
      siteName?: string; // The site name of the item's URL (optional).
    };
  };
}

/**
 * Fetches the URL of an item based on the provided request.
 *
 * @param {GraphQLRequestClient} client - The GraphQL client to use for the request.
 * @param {ItemQueryRequest} request - The request containing the item ID and language.
 * @returns {Promise<ItemUrlResponse | null>} A promise that resolves to the item URL response or null if the request fails.
 */
export const getItemUrl = async (
  client: GraphQLRequestClient,
  request: ItemQueryRequest
): Promise<ItemUrlResponse | null> => {
  return await client
    .request<ItemUrlResponse>(GetItemUrlQuery, {
      id: request.id,
      lang: request.language,
    })
    .catch((error) => {
      console.error(`Failed to get item URL for item ID: ${request.id}`, error);
      return null;
    });
};

/**
 * The GraphQL query to fetch the URL of an item.
 */
const GetItemUrlQuery = /* GraphQL */ `
  query GetItemUrl($id: String, $lang: String = "en") {
    item(path: $id, language: $lang) {
      url {
        path
        siteName
        hostName
        scheme
      }
      path
      id
    }
  }
`;
